/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AdminContextService } from '../../shared/services/admin-context.service';
import {
  AuditSearchViewModel,
  AuditLogSearchReturnData,
  AuditLogDetailReturnModel,
  AuditLogDetailReturnViewModel,
  AuditSearchPageView
} from '../audit.models';
import { ApiService } from '../../shared/services/api.service';
import { AUDIT_ENDPOINTS } from './audit.constants';
import { ApiResult } from '../../shared/model/api.model';

/**
 * Provides a service for getting and searching audit record.
 */
@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private readonly adminContextService = inject(AdminContextService);
  private readonly apiService = inject(ApiService);
  private readonly contextService = inject(AdminContextService);

  /**
   * Gets the latest audit items for consumption in things like admin
   * dashboards - takes no parameters.
   * @returns The latest 10 audit items
   */
  public getSnapshot(): Observable<AuditSearchViewModel[]> {
    const lenderCode = this.adminContextService.lenderCode;
    return this.apiService
      .request<ApiResult<AuditLogSearchReturnData>>({
        urlObj: AUDIT_ENDPOINTS.getAuditLatest,
        urlIds: {
          lendercode: lenderCode
        }
      })
      .pipe(
        map((resp) => {
          if (resp.isSuccess) {
            return resp.data.data.map((a) => {
              return {
                action: a.act,
                category: a.col,
                id: a.id,
                timestamp: new Date(a.ts),
                user: a.user,
                summary: a.desc
              };
            });
          }
          return [];
        })
      );
  }

  /**
   * Searches audit records, if no search is provided it just gets the latest page of specified
   * page size.
   * @param search The search can be null empty, or a simple query to do a search wit
   * @param minDate (optional) The lower bound date for filtering
   * @param maxDate (optional) The uppoer bound date for filtering
   * @param pageSize The page size - can be `1`-`200` in size
   * @param pageIndex The page index, must be `>=0`
   * @returns An array of search results.
   */
  public search(
    search: string = '',
    minDate?: Date,
    maxDate?: Date,
    pageSize?: number,
    pageIndex?: number
  ): Observable<AuditSearchPageView> {
    const lenderCode = this.adminContextService.lenderCode;
    return this.apiService
      .request<ApiResult<AuditLogSearchReturnData>>({
        urlObj: AUDIT_ENDPOINTS.searchAudit,
        urlIds: {
          lendercode: lenderCode
        },
        body: {
          search,
          pageIndex: Math.max(0, pageIndex),
          pageSize: Math.max(0, Math.min(pageSize, 200)),
          minDate,
          maxDate
        }
      })
      .pipe(
        map((resp) => {
          if (resp.isSuccess) {
            return {
              total: resp.data.total,
              data: resp.data.data.map((a) => {
                return {
                  action: a.act,
                  category: a.col,
                  id: a.id,
                  timestamp: new Date(a.ts),
                  user: a.user,
                  summary: a.desc
                };
              })
            };
          }
          return { data: [] };
        })
      );
  }

  /**
   * Finds and gets details for a specific audit record.
   * @param id The id of the audit record to get details for
   * @returns Observable of the matching audit detail for the id, if found, otherwise null.
   */
  public getDetails(id: string): Observable<AuditLogDetailReturnViewModel> {
    const lenderCode = this.adminContextService.lenderCode;
    return this.apiService
      .request<ApiResult<AuditLogDetailReturnModel>>({
        urlObj: AUDIT_ENDPOINTS.getAuditDetails,
        urlIds: {
          lendercode: lenderCode,
          id
        }
      })
      .pipe(
        map((resp) => {
          if (resp.isSuccess) {
            return {
              id: resp.data.id,
              lender: resp.data.lender,
              action: resp.data.act,
              category: resp.data.col,
              summary: resp.data.desc,
              origin: resp.data.ori,
              after: resp.data.after,
              recordId: resp.data.rid,
              timestamp: new Date(resp.data.ts),
              before: resp.data.before,
              user: resp.data.user
            };
          }
          return null;
        })
      );
  }

  /**
   * Audits a login event - this will return a 201 status if successful.
   * @returns An observable - success or failure determined by status code
   */
  public auditLogin(): Observable<void> {
    return new Observable<void>((sub) => {
      const readySub = this.adminContextService.appInitialized$.subscribe(() => {
        const lenderCode = this.adminContextService.lenderCode;
        this.apiService
          .request<void>({
            urlObj: AUDIT_ENDPOINTS.auditLoginEvent,
            urlIds: {
              lendercode: lenderCode
            }
          })
          .subscribe(() => sub.complete());
        readySub.unsubscribe();
      });
    });
  }

  /**
   * Helper method that does deep comparison between objects.
   * @returns the map function returns a new object with the diff clearly shown
   */
  public deepDiffMapper(maxDepth = 10, maxItems = 1000) {
    const seen = new WeakSet();
    let itemCount = 0;

    const VALUE_CREATED = 'created';
    const VALUE_UPDATED = 'updated';
    const VALUE_DELETED = 'deleted';
    const VALUE_UNCHANGED = '---';

    const isFunction = (x: any) => Object.prototype.toString.call(x) === '[object Function]';
    const isArray = (x: any) => Array.isArray(x);
    const isDate = (x: any) => Object.prototype.toString.call(x) === '[object Date]';
    const isObject = (x: any) => x && typeof x === 'object' && !Array.isArray(x);
    const isValue = (x: any) => !isObject(x) && !isArray(x);

    const compareValues = (value1: any, value2: any) => {
      if (value1 === value2) return VALUE_UNCHANGED;
      if (isDate(value1) && isDate(value2) && value1.getTime() === value2.getTime()) return VALUE_UNCHANGED;
      if (value1 === undefined) return VALUE_CREATED;
      if (value2 === undefined) return VALUE_DELETED;
      return VALUE_UPDATED;
    };

    const diffRecursive = (obj1: any, obj2: any, depth = 0): any => {
      if (depth > maxDepth || itemCount > maxItems) return;

      if (isFunction(obj1) || isFunction(obj2)) {
        throw new Error('Invalid argument. Function given, object expected.');
      }

      // 🆕 Handle explicit delete case
      if (obj1 && obj2 === null) {
        itemCount++;
        return {
          type: VALUE_DELETED,
          original: obj1,
          updated: null
        };
      }

      if (isValue(obj1) || isValue(obj2)) {
        itemCount++;
        return {
          type: compareValues(obj1, obj2),
          original: obj1,
          updated: obj2
        };
      }

      if (isArray(obj1) || isArray(obj2)) {
        const maxLength = Math.max(obj1?.length || 0, obj2?.length || 0);
        const arrayDiff = [];

        for (let i = 0; i < maxLength; i++) {
          const val1 = obj1?.[i];
          const val2 = obj2?.[i];
          const subDiff = diffRecursive(val1, val2, depth + 1);
          arrayDiff.push(subDiff);
        }

        itemCount += arrayDiff.length;
        return arrayDiff;
      }

      if (seen.has(obj1) || seen.has(obj2)) return;
      if (isObject(obj1)) seen.add(obj1);
      if (isObject(obj2)) seen.add(obj2);

      const diffResult: any = {};
      const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

      for (const key of keys) {
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];
        const mapped = diffRecursive(val1, val2, depth + 1);
        if (mapped !== undefined) {
          diffResult[key] = mapped;
        }
      }

      itemCount += Object.keys(diffResult).length;
      if (Object.keys(diffResult).length > 0) {
        return {
          type: compareValues(obj1, obj2),
          original: obj1,
          updated: obj2,
          ...diffResult
        };
      }

      return undefined;
    };

    return {
      VALUE_CREATED,
      VALUE_UPDATED,
      VALUE_DELETED,
      VALUE_UNCHANGED,
      isFunction,
      isArray,
      isDate,
      isObject,
      isValue,
      compareValues,
      map: diffRecursive
    };
  }

  /**
   * Helper method flattens json to list of key value pairs
   */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public flattenObject(obj: any): any {
    if (!this.isObject(obj)) {
      return JSON.stringify(obj);
    }
    const pairsFromJson = Object.entries(obj);
    return this.getFlattenedResults(pairsFromJson);
  }

  private isObject(value) {
    const type = typeof value;
    return value != null && (type === 'object' || type === 'function');
  }

  /**
   * Helper method to recursively combine flattened pairs.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getFlattenedResults(pairs: any[]) {
    const resultSoFar = {};

    // eslint-disable-next-line guard-for-in, @typescript-eslint/no-for-in-array
    for (const item in pairs) {
      this.flattenPairs(pairs[item][1], pairs[item][0], resultSoFar);
    }
    return resultSoFar;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public flattenPairs(obj: any, prefix: string, resultSoFar: any): [string, any][] {
    if (obj?.hasOwnProperty('original')) {
      // eslint-disable-next-line eqeqeq
      if (obj.type == 'updated') {
        resultSoFar[prefix] = {
          newValue: obj.updated,
          oldValue: obj.original
        };
      }
      // eslint-disable-next-line eqeqeq
      if (obj.type == 'created' && this.isObject(obj.updated)) {
        // eslint-disable-next-line guard-for-in
        for (const item in obj.updated) {
          const newPrefix = prefix + '.' + item;
          resultSoFar[newPrefix] = {
            newValue: obj.updated[item],
            oldValue: obj.original
          };
        }
        // eslint-disable-next-line eqeqeq
      } else if (obj.type == 'deleted' && this.isObject(obj.original)) {
        // eslint-disable-next-line guard-for-in
        for (const item in obj.original) {
          const newPrefix = prefix + '.' + item;
          resultSoFar[newPrefix] = {
            newValue: obj.updated,
            oldValue: obj.original[item]
          };
        }
      } else {
        resultSoFar[prefix] = {
          newValue: obj.updated,
          oldValue: obj.original
        };
      }
    }
    if (!this.isObject(obj)) {
      return [prefix, obj];
    }
    const pairsFromJson = Object.entries(obj);
    return this.combinePairsWithPrefix(pairsFromJson, prefix, resultSoFar);
  }

  /**
   * Helper method to combine key value pairs with appropriate prefix.
   */
  public combinePairsWithPrefix(pairs, prefix, resultSoFar) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let final: [string, any][] = [];

    // eslint-disable-next-line guard-for-in
    for (const item in pairs) {
      let flattened;
      if (!isNaN(parseFloat(pairs[item][0]))) {
        flattened = this.flattenPairs(pairs[item][1], `${prefix}[${pairs[item][0]}]`, resultSoFar);
      } else {
        flattened = this.flattenPairs(pairs[item][1], `${prefix}.${pairs[item][0]}`, resultSoFar);
      }
      if (flattened.length === 2 && !this.isObject(flattened[0]) && !this.isObject(flattened[1])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        final = final.concat([flattened as [string, any]]);
      } else {
        final = final.concat(flattened);
      }
    }
    return final;
  }
}
